/**
 * Nordic nRF54L LM20 RT 系列鼠标设备上报回填规则集 (Phase 4)
 *
 * TODO 检查是否需要共享
 *
 * 监听 `report:Mouse_user_data` 事件,把硬件主动上报的高频字段(当前 CPI 档位、配置索引、回报率、
 * 按键扫描率等)回填到缓存,让后续 `getCurrentGear()` / `getCurrentConfigIndex()` /
 * `getMousePerfCfg()` 等调用直接命中缓存,不再触发硬件读。
 *
 * --- 关键约束 ---
 *
 * 1. **字节布局必须与 sendAndWait 真实返回一致** (Requirement 4.7):
 *    回填的 `BackfillItem.data` 布局为 `[ACK_BYTE, CMD_BYTE, LENGTH, 0x00, ...actualData]`,
 *    其中 ACK_BYTE = 0x01 (read_ack_value),这与 `sendAndWait` feature report 响应格式一致。
 *    parser 统一从 offset 4 开始读取有效数据。
 *
 * 2. **extract 是纯函数** (Requirement 4.8):
 *    不调用 sendRead / sendWrite / channel.send 等 SDK 方法。
 *
 * 3. **只回填高频字段** (design Components §4):
 *    当前覆盖:
 *      - 当前 CPI 档位(cpiLev) → MOUSE_DPI_CUR (按 cfgIdx 取配置地址)
 *      - 当前配置索引(cfgSel) → CONFIG_CURRENT
 *      - 当前回报率(rpt) → MOUSE_REPORT (按 cfgIdx 取配置地址)
 *      - 当前按键扫描率(kRate) → MOUSE_SCAN_RATE (按 cfgIdx 取配置地址)
 *    电量(batPercent)暂不回填(电量无对应 sendRead 调用路径)。
 *
 * --- 地址映射说明 ---
 *
 * 鼠标的 DPI 当前地址 `MOUSE_DPI_CUR` 是一个按配置索引偏移的多配置地址,
 * 通过 `getAddress(ADDRESS, cfgIdx)` 统一解析,与业务读取路径保持一致。
 *
 * 回报率(rpt) 和按键扫描率(kRate) 同样跟随配置索引偏移。
 *
 * @packageDocumentation
 */
import { CMD_LM20_RT, PROTOCOL_CONFIG_LM20_RT } from './cmd'
import { getAddress } from '../../../utils/common'
import { BackfillItem, makeKey, ReportBackfillRule } from '../../../cache'
import { mouseUserCustom } from '@rapoo/device-sdk'
import { ADDRESS_LM20_RT } from './address'

/** ACK 字节 - 与 sendAndWait 真实返回的 ACK 头一致 */
const READ_ACK = PROTOCOL_CONFIG_LM20_RT.read_ack_value // 0x01
const READ_EEPROM = CMD_LM20_RT.read_eeprom[0]

/**
 * 构造与 sendAndWait 返回格式一致的字节快照。
 *
 * Feature report 响应布局:
 * - Byte 0: ACK (0x01 = 读取成功)
 * - Byte 1: CMD echo (0xa4 = read_eeprom)
 * - Byte 2: length
 * - Byte 3: reserved (0x00)
 * - Byte 4+: actual data bytes
 *
 * @param actualData 实际数据字节(从 offset 4 开始的部分)
 * @param length 协议层声明的读取长度
 */
function buildCacheBytes(actualData: number[], length: number): Uint8Array {
  const header = [READ_ACK, READ_EEPROM, length, 0x00]
  return new Uint8Array([...header, ...actualData])
}

/**
 * 从 getAddress 的结果中提取数值形式的地址起始值。
 * 用于 `BackfillItem.addressStart` 字段。
 */
function extractAddressStart(addr: ReturnType<typeof getAddress>): number {
  if (typeof addr === 'number') return addr
  return (addr[0] | (addr[1] << 8)) >>> 0
}

/**
 * TODO Nordic nRF54L 鼠标上报回填规则集。
 *
 * 当前仅订阅 `report:Mouse_user_data` 事件,覆盖:
 * - 当前 CPI 档位 (cpiLev) → MOUSE_DPI_CUR (按 cfgIdx 取配置地址)
 * - 当前配置索引 (cfgSel) → CONFIG_CURRENT
 * - 当前回报率 (rpt) → MOUSE_REPORT (按 cfgIdx 取配置地址)
 * - 当前按键扫描率 (kRate) → MOUSE_SCAN_RATE (按 cfgIdx 取配置地址)
 * - 电池百分比 (batPercent) — 暂不回填(电量无对应 sendRead 调用路径)
 */
export const LM20_RT_MOUSE_REPORT_RULES: ReportBackfillRule<mouseUserCustom>[] = [
  {
    event: 'report:Mouse_user_data',
    extract: (userData: mouseUserCustom): BackfillItem[] => {
      const items: BackfillItem[] = []
      const cfgIdx = userData.cfgSel ?? 0

      // ==================== 1. 当前 CPI 档位 (cpiLev) ====================
      // getCurrentGear() 调用: sendRead(device, getAddress(MOUSE_DPI_CUR, cfgIdx), 1, parser)
      // parser: data.getUint8(4)
      const dpiCurAddr = getAddress(ADDRESS_LM20_RT.MOUSE_DPI_CUR, cfgIdx)
      items.push({
        key: makeKey(dpiCurAddr, 1),
        data: buildCacheBytes([userData.cpiLev], 1),
        tags: [`cfg:${cfgIdx}`, `dpi:current:${cfgIdx}`],
        addressStart: extractAddressStart(dpiCurAddr),
        length: 1,
      })

      // ==================== 2. 当前配置索引 (cfgSel) ====================
      // getCurrentConfigIndex() 调用: sendRead(device, getAddress(CONFIG_CURRENT, 0), 1, parser)
      // parser: data.getUint8(4)
      const cfgAddr = getAddress(ADDRESS_LM20_RT.CONFIG_CURRENT, 0)
      const cfgReadLength = 1
      items.push({
        key: makeKey(cfgAddr, cfgReadLength),
        data: buildCacheBytes([cfgIdx], cfgReadLength),
        tags: [`cfg:${cfgIdx}`],
        addressStart: extractAddressStart(cfgAddr),
        length: cfgReadLength,
      })

      // ==================== 3. 当前回报率 (rpt) ====================
      // 回报率读取调用: sendRead(device, getAddress(MOUSE_REPORT, cfgIdx), 1, parser)
      // parser: data.getUint8(4)
      // getReportRate() 已注册 cfg tag,回填带上同 tag 确保失效联动一致。
      if (userData.rpt !== undefined) {
        const rptAddr = getAddress(ADDRESS_LM20_RT.MOUSE_REPORT, cfgIdx)
        items.push({
          key: makeKey(rptAddr, 1),
          data: buildCacheBytes([userData.rpt], 1),
          tags: [`cfg:${cfgIdx}`],
          addressStart: extractAddressStart(rptAddr),
          length: 1,
        })
      }

      // ==================== 4. 当前按键扫描率 (kRate) ====================
      // 扫描率读取调用: sendRead(device, getAddress(MOUSE_SCAN_RATE, cfgIdx), 1, parser)
      // parser: data.getUint8(4)
      // getScanRate() 已注册 cfg tag,回填带上同 tag 确保失效联动一致。
      if (userData.kRate !== undefined) {
        const kRateAddr = getAddress(ADDRESS_LM20_RT.MOUSE_SCAN_RATE, cfgIdx)
        items.push({
          key: makeKey(kRateAddr, 1),
          data: buildCacheBytes([userData.kRate], 1),
          tags: [`cfg:${cfgIdx}`],
          addressStart: extractAddressStart(kRateAddr),
          length: 1,
        })
      }

      return items
    },
  },
]
